/**
 * Fix builder. The guided way to construct a fix that preserves its
 * observations, our differentiator. Add observations one at a time, then
 * compute the fix from two or more. The construction runs through the tested
 * buildFix, so the result carries its lines of position, a banded confidence,
 * and a cocked-hat zone when the lines do not concur.
 *
 * Three observation types: a bearing on a mark, a range off a mark, and a
 * transit (two marks in line, the strongest line of position because it needs
 * no instrument, just the eye lining up two objects).
 *
 * A note on the flow: arming a mark pick closes this sheet so the chart
 * underneath is tappable, then capturing a mark reopens it. A transit needs two
 * marks, so it picks the front, then the rear.
 */
import { useState } from 'react';
import { useShellStore, type DraftObs } from '@/store/shell';
import { useObjectsStore, nextObjectId } from '@/store/objects';
import { buildFix, type FixObservation } from '@/chartwork';
import { formatBearing } from '@/nav';
import { Sheet } from './Sheet';

let draftSeq = 1;

type ObsType = 'bearing' | 'range' | 'transit';
type Mark = { position: [number, number]; label: string };

function describe(d: DraftObs): string {
  if (d.type === 'bearing') return `${d.markLabel} · bearing ${formatBearing(d.bearing ?? 0)}`;
  if (d.type === 'range') return `${d.markLabel} · range ${d.range} ${d.unit}`;
  return `${d.markLabel} and ${d.rearLabel} · transit`;
}

export function FixBuilderSheet() {
  const fixDraft = useShellStore((s) => s.fixDraft);
  const pendingMark = useShellStore((s) => s.pendingMark);
  const armFixPick = useShellStore((s) => s.armFixPick);
  const addFixObs = useShellStore((s) => s.addFixObs);
  const removeFixObs = useShellStore((s) => s.removeFixObs);
  const clearFixDraft = useShellStore((s) => s.clearFixDraft);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const [type, setType] = useState<ObsType>('bearing');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<'nm' | 'cables' | 'm'>('nm');
  // The transit front mark, held while we pick the rear mark.
  const [transitFront, setTransitFront] = useState<Mark | null>(null);

  function chooseType(next: ObsType) {
    setType(next);
    setTransitFront(null);
  }

  function addBearingOrRange() {
    const num = Number.parseFloat(value);
    if (!pendingMark || Number.isNaN(num)) return;
    const obs: DraftObs = {
      id: `d-${draftSeq++}`,
      type,
      markPosition: pendingMark.position,
      markLabel: pendingMark.label,
      ...(type === 'bearing' ? { bearing: num } : { range: num, unit }),
    };
    addFixObs(obs);
    setValue('');
  }

  function addTransit() {
    if (!transitFront || !pendingMark) return;
    addFixObs({
      id: `d-${draftSeq++}`,
      type: 'transit',
      markPosition: transitFront.position,
      markLabel: transitFront.label,
      rearPosition: pendingMark.position,
      rearLabel: pendingMark.label,
    });
    setTransitFront(null);
  }

  function compute() {
    const positions: Record<string, [number, number]> = {};
    const observations: FixObservation[] = fixDraft.map((d) => {
      if (d.type === 'transit') {
        const front = `markF-${d.id}`;
        const rear = `markR-${d.id}`;
        positions[front] = d.markPosition;
        positions[rear] = d.rearPosition ?? d.markPosition;
        return {
          id: `obs-${d.id}`,
          kind: 'transit_obs',
          frontObject: front,
          rearObject: rear,
          source: { method: 'manual' },
          parents: [],
          children: [],
          userPlaced: true,
        };
      }
      const markId = `mark-${d.id}`;
      positions[markId] = d.markPosition;
      if (d.type === 'bearing') {
        return {
          id: `obs-${d.id}`,
          kind: 'bearing_obs',
          object: markId,
          bearing: d.bearing ?? 0,
          bearingType: 'true',
          source: { method: 'manual' },
          parents: [],
          children: [],
          userPlaced: true,
        };
      }
      return {
        id: `obs-${d.id}`,
        kind: 'range_obs',
        object: markId,
        range: d.range ?? 0,
        unit: d.unit ?? 'nm',
        source: { method: 'manual' },
        parents: [],
        children: [],
        userPlaced: true,
      };
    });

    const built = buildFix({
      observations,
      positions,
      time: { iso: new Date().toISOString() },
      newId: nextObjectId,
    });
    if (!built) {
      showToast('Need two or more observations that resolve to a line');
      return;
    }
    useObjectsStore
      .getState()
      .putObjects([...observations, ...built.lops, built.fix, ...(built.zone ? [built.zone] : [])]);
    clearFixDraft();
    closeSheet();
    showToast(`Fix plotted, confidence ${built.fix.confidence?.label ?? '—'}`);
  }

  return (
    <Sheet
      name="fixbuilder"
      title="Plot a fix"
      sub="Add two or more observations, then compute. The fix keeps the observations and lines that made it, and shows the cocked hat if they do not meet cleanly."
    >
      {fixDraft.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {fixDraft.map((d) => (
            <div className="acp-kv" key={d.id}>
              <span className="k">{describe(d)}</span>
              <button
                type="button"
                className="v"
                style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer' }}
                onClick={() => removeFixObs(d.id)}
                aria-label="Remove observation"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingMark ? (
        <div>
          <div className="acp-seg">
            <button type="button" className={type === 'bearing' ? 'on' : ''} onClick={() => chooseType('bearing')}>
              Bearing
            </button>
            <button type="button" className={type === 'range' ? 'on' : ''} onClick={() => chooseType('range')}>
              Range
            </button>
            <button type="button" className={type === 'transit' ? 'on' : ''} onClick={() => chooseType('transit')}>
              Transit
            </button>
          </div>

          {type === 'transit' ? (
            transitFront ? (
              <>
                <p className="sub" style={{ marginBottom: 8 }}>
                  Front: <b>{transitFront.label}</b>, rear: <b>{pendingMark.label}</b>. Two marks in line.
                </p>
                <div className="acp-btn-row">
                  <button type="button" className="acp-btn primary" onClick={addTransit}>
                    Add transit
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="sub" style={{ marginBottom: 8 }}>
                  Front mark: <b>{pendingMark.label}</b>. Now pick the rear mark, the one in line behind it.
                </p>
                <div className="acp-btn-row">
                  <button
                    type="button"
                    className="acp-btn primary"
                    onClick={() => {
                      setTransitFront(pendingMark);
                      armFixPick();
                      showToast('Tap the rear mark');
                    }}
                  >
                    Pick rear mark
                  </button>
                </div>
              </>
            )
          ) : (
            <>
              <p className="sub" style={{ marginBottom: 8 }}>
                Mark: <b>{pendingMark.label}</b>. What did we observe on it?
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="acp-note-input"
                  style={{ marginTop: 0 }}
                  inputMode="decimal"
                  placeholder={type === 'bearing' ? 'Bearing in degrees true' : 'Range'}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                {type === 'range' && (
                  <select value={unit} onChange={(e) => setUnit(e.target.value as 'nm' | 'cables' | 'm')}>
                    <option value="nm">nm</option>
                    <option value="cables">cables</option>
                    <option value="m">m</option>
                  </select>
                )}
              </div>
              <div className="acp-btn-row">
                <button type="button" className="acp-btn primary" onClick={addBearingOrRange}>
                  Add observation
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              armFixPick();
              showToast('Tap a mark on the chart');
            }}
          >
            Add observation
          </button>
          <button
            type="button"
            className="acp-btn primary"
            disabled={fixDraft.length < 2}
            style={{ opacity: fixDraft.length < 2 ? 0.5 : 1 }}
            onClick={compute}
          >
            Compute fix ({fixDraft.length})
          </button>
        </div>
      )}

      {fixDraft.length > 0 && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={clearFixDraft}>
            Clear all
          </button>
        </div>
      )}
    </Sheet>
  );
}
